/* Original product photos stay intact. Busy-background photos use reviewed
   normalized subject bounds; plain-background photos may trim only empty margins.
   Spec sheets and product-detail galleries are deliberately outside this selector. */
(() => {
  'use strict';
  if (window.tsProductFramingInitialized) return;
  window.tsProductFramingInitialized = true;
  const selector = '#MainContent .product-card .product-image > img, #MainContent .tsc-collection .product-photo > img, #MainContent .vault-guide .vg-product > .listing-photo > img, #MainContent .card-wrapper[data-product] .media > img';
  const records = window.TSProductPhotoBounds || {};
  const states = new WeakMap();
  const cache = new Map();
  const full = [0, 0, 1, 1];
  let pending = [];
  let running = 0;

  function identity(img) {
    try {
      const url = new URL(img.currentSrc || img.src, location.href);
      const name = decodeURIComponent(url.pathname.split('/').pop()).toLowerCase();
      return {url, name, key: url.pathname + '?v=' + (url.searchParams.get('v') || '')};
    } catch (_) { return null; }
  }

  // A strict white/transparent-border check never guesses at a busy background.
  function emptyMargins(data, w, h) {
    const blank = (x, y) => {
      const i = (y * w + x) * 4;
      return data[i + 3] < 8 || (data[i] >= 249 && data[i + 1] >= 249 && data[i + 2] >= 249);
    };
    for (let x = 0; x < w; x++) if (!blank(x, 0) || !blank(x, h - 1)) return full;
    for (let y = 0; y < h; y++) if (!blank(0, y) || !blank(w - 1, y)) return full;
    let l = w, t = h, r = -1, b = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      if (!blank(x, y)) { l = Math.min(l, x); t = Math.min(t, y); r = Math.max(r, x); b = Math.max(b, y); }
    }
    if (r < l || b < t) return full;
    // Guard fine edges, pale surfaces, labels and separate accessories.
    const pad = Math.max(3, Math.ceil(Math.max(w, h) * .025));
    l = Math.max(0, l - pad); t = Math.max(0, t - pad);
    r = Math.min(w, r + pad + 1); b = Math.min(h, b + pad + 1);
    if ((r - l) / w < .12 || (b - t) / h < .06) return full;
    return [l / w, t / h, (r - l) / w, (b - t) / h];
  }

  function measureImage(img) {
    const factor = Math.min(1, 320 / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * factor));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * factor));
    const ctx = canvas.getContext('2d', {willReadFrequently: true});
    if (!ctx) return full;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return emptyMargins(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height);
  }

  function pump() {
    if (running >= 2 || !pending.length) return;
    const job = pending.shift(); running++;
    job().finally(() => { running--; pump(); });
    pump();
  }

  function boundsFor(img, source) {
    const record = records[source.name];
    const version = source.url.searchParams.get('v');
    if (record && (!record.v || record.v === version) && Math.abs(img.naturalWidth / img.naturalHeight / record.a - 1) < .035) {
      return Promise.resolve(record.b);
    }
    if (cache.has(source.key)) return cache.get(source.key);
    const promise = new Promise(resolve => {
      pending.push(async () => {
        try { resolve(measureImage(img)); return; } catch (_) { /* CDN may need anonymous CORS. */ }
        if (!(source.url.hostname === location.hostname || /(^|\.)shopify\.com$/.test(source.url.hostname) || source.url.hostname === 'telecomspecialties.com' || source.url.hostname === 'www.telecomspecialties.com')) { resolve(full); return; }
        await new Promise(done => {
          const probe = new Image();
          const timer = setTimeout(() => finish(full), 5000);
          let finished = false;
          function finish(bounds) {
            if (finished) return; finished = true;
            clearTimeout(timer); probe.onload = probe.onerror = null; resolve(bounds); done();
          }
          probe.crossOrigin = 'anonymous';
          probe.onload = () => { try { finish(measureImage(probe)); } catch (_) { finish(full); } };
          probe.onerror = () => finish(full);
          const url = new URL(source.url); url.searchParams.set('width', '320');
          probe.src = url.href;
        });
      });
      pump();
    });
    cache.set(source.key, promise);
    return promise;
  }

  function layout(img, state) {
    if (!img.isConnected || !state.bounds || !img.naturalWidth || !img.naturalHeight) return;
    const frame = img.parentElement;
    const w = frame.clientWidth, h = frame.clientHeight;
    if (!w || !h) return;
    const [x, y, bw, bh] = state.bounds;
    const inset = Math.max(4, Math.min(w, h) * .035);
    const scale = Math.min((w - inset * 2) / (bw * img.naturalWidth), (h - inset * 2) / (bh * img.naturalHeight));
    const width = img.naturalWidth * scale, height = img.naturalHeight * scale;
    img.style.setProperty('--ts-photo-width', width.toFixed(3) + 'px');
    img.style.setProperty('--ts-photo-height', height.toFixed(3) + 'px');
    img.style.setProperty('--ts-photo-left', ((w - bw * width) / 2 - x * width).toFixed(3) + 'px');
    img.style.setProperty('--ts-photo-top', ((h - bh * height) / 2 - y * height).toFixed(3) + 'px');
    frame.setAttribute('data-ts-photo-frame', '');
    img.setAttribute('data-ts-photo-fit', '');
  }

  const resize = 'ResizeObserver' in window ? new ResizeObserver(entries => {
    for (const {target} of entries) for (const img of target.querySelectorAll(':scope > img')) {
      const state = states.get(img); if (state) layout(img, state);
    }
  }) : null;

  async function fit(img) {
    if (!img.complete || !img.naturalWidth) return;
    const state = states.get(img), source = identity(img);
    if (!state || !source) return;
    if (state.key === source.key) { layout(img, state); return; }
    state.key = source.key;
    // Clear the old framing before a variant/photo replacement is evaluated.
    state.bounds = null; img.removeAttribute('data-ts-photo-fit');
    const key = source.key;
    const bounds = await boundsFor(img, source);
    if (!img.isConnected || state.key !== key) return;
    state.bounds = bounds;
    layout(img, state);
  }

  const visible = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { fit(entry.target); visible.unobserve(entry.target); }
  }, {rootMargin: '240px'}) : null;

  function scan(root) {
    const images = root.matches?.(selector) ? [root] : root.querySelectorAll?.(selector) || [];
    for (const img of images) {
      if (states.has(img)) {
        if (resize) resize.observe(img.parentElement);
        if (visible) visible.observe(img); else fit(img);
        continue;
      }
      states.set(img, {key: null, bounds: null});
      img.addEventListener('load', () => fit(img));
      if (resize) resize.observe(img.parentElement);
      if (visible) visible.observe(img); else fit(img);
    }
  }

  function init() {
    const main = document.getElementById('MainContent');
    if (!main) return;
    scan(main);
    new MutationObserver(mutations => {
      for (const change of mutations) {
        if (change.type === 'attributes') { if (states.has(change.target)) fit(change.target); }
        else {
          for (const added of change.addedNodes) if (added.nodeType === 1) scan(added);
          for (const removed of change.removedNodes) if (removed.nodeType === 1 && !removed.isConnected) {
            const imgs = removed.tagName === 'IMG' ? [removed] : removed.querySelectorAll('img');
            for (const img of imgs) if (states.has(img)) { visible?.unobserve(img); if (img.parentElement) resize?.unobserve(img.parentElement); }
          }
        }
      }
    }).observe(main, {subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset']});
    window.addEventListener('resize', () => { for (const img of main.querySelectorAll(selector)) fit(img); }, {passive: true});
    document.addEventListener('shopify:section:load', event => scan(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true}); else init();
})();
